import type { Item, Port, SignalType } from '../model/schema.js';

/** Sources can have up to this many outputs (sound cards, playback rigs – U30). */
export const MAX_OUTPUTS = 64;

const AUDIO: ReadonlySet<SignalType> = new Set<SignalType>(['mic', 'line', 'instrument']);

/** Port id suffix and name suffix of output `k` (0-based) of `n`: '' / L,R / 1..n. */
export function outputSuffix(k: number, n: number): { id: string; name: string } {
  if (n === 1) return { id: '', name: '' };
  if (n === 2) return k === 0 ? { id: 'l', name: ' L' } : { id: 'r', name: ' R' };
  return { id: String(k + 1), name: ` ${k + 1}` };
}

/**
 * The audio outputs a channel can be fed from, in port order. A DI's outputs are its mic-level
 * ports (its instrument-level thru ports pass the signal on, they are no source).
 */
export function sourceOutputs(item: Pick<Item, 'kind' | 'ports'>): Port[] {
  return (item.ports ?? []).filter(
    (p) =>
      p.direction === 'out' && AUDIO.has(p.signal) && (item.kind !== 'di' || p.signal === 'mic'),
  );
}

export function outputCount(item: Pick<Item, 'kind' | 'ports'>): number {
  return sourceOutputs(item).length;
}

export interface OutputChange {
  item: Item;
  /** Old output port id → new port id, or null when that output is gone. */
  portMap: Record<string, string | null>;
}

/**
 * The item with `n` outputs (1–64): mono `out`, stereo `outl`/`outr`, else `out1`…`outN`. A DI
 * gets an input and a thru per output. Signal, connector and +48V follow the first existing
 * output (defaults: DI mic/XLR, others line/jack); other ports (power, RF, network …) stay.
 */
export function withOutputCount(item: Item, n: number): OutputChange {
  const count = Math.min(MAX_OUTPUTS, Math.max(1, Math.round(n)));
  const old = sourceOutputs(item);
  const isDi = item.kind === 'di';
  const template = old[0];
  const signal: SignalType = template?.signal ?? (isDi ? 'mic' : 'line');
  const connector = template?.connector ?? (isDi ? 'xlr3' : 'trs');
  const phantom = template?.phantom;
  const oldIds = new Set(old.map((p) => p.id));
  const isDiPassive = (p: Port) =>
    isDi && p.signal === 'instrument' && (p.direction === 'in' || p.direction === 'out');
  const kept = (item.ports ?? []).filter((p) => !oldIds.has(p.id) && !isDiPassive(p));
  const fresh: Port[] = [];
  for (let k = 0; k < count; k++) {
    const s = outputSuffix(k, count);
    if (isDi)
      fresh.push({
        id: `in${s.id}`,
        name: `In${s.name}`,
        direction: 'in',
        signal: 'instrument',
        connector: 'ts',
      });
    fresh.push({
      id: `out${s.id}`,
      name: `Out${s.name}`,
      direction: 'out',
      signal,
      connector,
      ...(phantom !== undefined ? { phantom } : {}),
    });
    if (isDi)
      fresh.push({
        id: `thru${s.id}`,
        name: `Thru${s.name}`,
        direction: 'out',
        signal: 'instrument',
        connector: 'ts',
      });
  }
  const outs = fresh.filter((p) => p.direction === 'out' && (!isDi || p.signal === 'mic'));
  const portMap: Record<string, string | null> = {};
  old.forEach((p, i) => (portMap[p.id] = outs[i]?.id ?? null));
  return { item: { ...item, ports: [...fresh, ...kept] }, portMap };
}
